import React from 'react';
import Button from 'Components/Button';
import PropTypes from 'prop-types';
import { getTabByTarget, tabTypes } from 'Utils/getContactTab';
import { exchangeLabelIfNecessary, exchangeTrackingLabelIfNecessary } from 'Utils/getButtonLabel';
import onContactButtonClick from 'Utils/onContactButtonClick';
import { validButtonVariants } from 'Utils';
import { withUrlParams } from 'Utils/withUrlParams';
import { withRouter } from 'react-router-dom';
import { getAdjustedModalType } from 'Utils/getModalType';

export const ctaPropTypes = {
  additional: PropTypes.string,
  buttonVariant: PropTypes.oneOf(validButtonVariants),
  className: PropTypes.string,
  disableAdaptiveLabel: PropTypes.bool,
  disableAdaptiveTracking: PropTypes.bool,
  href: PropTypes.string,
  label: PropTypes.string,
  light: PropTypes.bool,
  mazdaForm: PropTypes.bool,
  model: PropTypes.string,
  onOpenContact: PropTypes.func,
  openContactModal: PropTypes.bool,
  screenReaderLabel: PropTypes.string,
  small: PropTypes.bool,
  trackingLabel: PropTypes.string,
  type: PropTypes.oneOf(tabTypes),
  forceUppercase: PropTypes.bool,
};

export const getScreenReaderLabelFromAdditionalProperty = (
  additional,
  defaultValue = '',
  label = ''
) => {
  const params = Object.fromEntries(new URLSearchParams(additional));
  let carmodel = '';
  if (params.carmodel) {
    carmodel = window.hwsconfig.carlines.find(
      ({ deeplink }) => deeplink === params.carmodel
    )?.label;
  }
  const seperator = label !== '' && '- ';
  if (params.modaltype === 'TEST_DRIVE' && carmodel) {
    return `${seperator}Öffnet den Dialog zur Anfrage einer Probefahrt für den ${carmodel}`;
  }
  if (params.modaltype === 'REQUEST_FOR_PROPOSAL' && carmodel) {
    return `${seperator}Öffnet den Dialog zur Anfrage eines Angebots für den ${carmodel}`;
  }
  if (params.modaltype === 'NEWSLETTER' && carmodel) {
    return `${seperator}Öffnet den Dialog zur Newsletteranmeldung für den ${carmodel}`;
  }
  if (params.accessibleLabel) {
    return `${seperator}${params.accessibleLabel} ${seperator}Öffnet einen Dialog`;
  }
  if (params.showmodal) {
    return `${seperator}Öffnet einen Dialog`;
  }

  // in case no screenReaderLabel is given, but a label
  // that is upper case it gets returned lower cased
  const value = defaultValue || label;
  const uCase = value.toUpperCase();
  const isUCase = uCase === value;

  return isUCase ? value.toLowerCase() : defaultValue;
};

class CallToAction extends React.Component {
  static propTypes = ctaPropTypes;

  static defaultProps = {
    additional: null,
    buttonVariant: 'primary',
    className: '',
    href: null,
    label: '',
    light: false,
    mazdaForm: false,
    model: '',
    onOpenContact: () => {},
    onClickMain: undefined,
    openContactModal: true,
    screenReaderLabel: '',
    small: false,
    target: null,
    trackingLabel: null,
    type: null,
    forceUppercase: undefined,
  };

  render() {
    const {
      additional,
      buttonVariant,
      className,
      disableAdaptiveLabel,
      disableAdaptiveTracking,
      forceUppercase,
      label,
      light,
      location,
      mazdaForm,
      model,
      onClickMain,
      onOpenContact,
      screenReaderLabel: srl,
      scrollToDeeplinkId,
      small,
      target,
      trackingLabel,
      type,
    } = this.props;

    const tabIndex = getTabByTarget(type);
    const track = !!(additional && additional === 'iframeContent');
    const openContactModalCallback = onContactButtonClick(
      !mazdaForm,
      () => onOpenContact && onOpenContact(tabIndex, model)
    );
    let { href } = this.props;

    const hrefIsCurrentLocation = href && href.includes(location.pathname);

    let onClick = null;

    if (tabIndex > -1) {
      onClick = openContactModalCallback;

      // do not follow links when a contact modal should be opened while
      // <Button>s `onClick` has no event for primary typed buttons.
      href = null;
    }

    if (hrefIsCurrentLocation) {
      onClick = () => {
        setTimeout(() => {
          scrollToDeeplinkId();
        }, 100);
      };
    }

    const { isOsbUser, isSalesPartner, isServicePartner } = window.dealer.settings;
    const adjustedModalType = getAdjustedModalType({
      isOsbUser,
      isSalesPartner,
      isServicePartner,
      initialModalType: undefined,
      targetHref: href,
    });

    // Sonderlocke: be explicit about the replacement (MAZ-2109)
    const displayLabel =
      !disableAdaptiveLabel && href && href.includes('?')
        ? exchangeLabelIfNecessary(label, adjustedModalType)
        : label;
    const hasSpecial = displayLabel.toLowerCase().includes('6e');
    const specialLabel = displayLabel.replaceAll('6E', '6e');

    return (
      <Button
        href={href}
        className={className}
        label={specialLabel}
        upperCase={forceUppercase === false ? forceUppercase : !hasSpecial}
        screenReaderLabel={getScreenReaderLabelFromAdditionalProperty(additional, srl, label)}
        light={light}
        onClick={onClickMain ? onClickMain : onClick}
        small={small}
        target={target}
        track={track}
        trackingLabel={
          disableAdaptiveTracking
            ? trackingLabel
            : exchangeTrackingLabelIfNecessary(trackingLabel, adjustedModalType)
        }
        variant={buttonVariant}
      />
    );
  }
}

export default withRouter(withUrlParams(CallToAction));
